import {
  createStore,
  emitOnce,
  filterNil,
  select,
  withProps,
} from '@ngneat/elf';
import {
  getAllEntities,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import {
  clearRequestsStatus,
  createRequestsStatusOperator,
  joinRequestResult,
  selectRequestStatus,
  updateRequestStatus,
  withRequestsStatus,
} from '@ngneat/elf-requests';
import { trackRequest } from '@tmx/core/elf-utils';
import {
  Driver,
  DriverGroup,
  DriverVehicleLink,
  OrphanedResource,
} from '@tmx/data-access';
import { RowsDescriptor, SortDirection } from '@tmx/operandi';
import { distinctUntilChanged, map, shareReplay, Subject } from 'rxjs';

import { DriversImportResponse } from '../../models/drivers-import-response';
import { EMPTY_ROWS_DESCRIPTOR } from '../../models/empty-rows-descriptor';

type RequestKeys = 'fetch-csv';

interface driversProps {
  creatingNewDriver: boolean;
  driverErrorMessage: null | string;
  driverRowsDescriptor: RowsDescriptor;
  hasDrivers: boolean;
  importResponse: DriversImportResponse;
  linkedData: Map<string, DriverVehicleLink> | null;
  offset: number;
  orphans: Map<string, OrphanedResource> | null;
  prefilledDriverId: null | string;
  query: string;
  selectedDriver: Driver | null;
  selectedDriversGroupIds: number[];
  selectedDriversGroups: DriverGroup[];
  sortDirection: '' | 'asc' | 'desc';
  sortExpression: string;
  totalDrivers: number;
}

const driversStore = createStore(
  { name: 'drivers' },
  withProps<driversProps>({
    totalDrivers: 0,
    hasDrivers: false,
    offset: 0,
    query: '',
    sortExpression: '',
    sortDirection: SortDirection.None,
    driverRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    selectedDriversGroups: [],
    selectedDriversGroupIds: [],
    selectedDriver: null,
    driverErrorMessage: null,
    creatingNewDriver: false,
    prefilledDriverId: null,
    orphans: null,
    importResponse: new DriversImportResponse(),
    linkedData: null,
  }),
  withEntities<Driver>(),
  withRequestsStatus<
    | 'delete-driver'
    | 'driver-details'
    | 'driver-import'
    | 'driver-update'
    | 'drivers'
    | 'drivers-groups'
    | 'edit-drivers-groups'
    | 'linked-data'
  >()
);

export const trackDriversRequests = <T>(keys: RequestKeys[]) =>
  trackRequest<T, RequestKeys>(keys);

export const joinDriversRequests = <T>(keys: RequestKeys[]) =>
  joinRequestResult<T>(keys);

export const fetchDriversCsvRequest$ = driversStore.pipe(
  joinDriversRequests(['fetch-csv']),
  map(({ status, fetchStatus, isSuccess }) => ({
    status,
    fetchStatus,
    isSuccess,
  })),
  distinctUntilChanged()
);

export const trackDriversRequestsStatus =
  createRequestsStatusOperator(driversStore);

export const driverErrorMessage$ = driversStore.pipe(
  select((state) => state.driverErrorMessage),
  filterNil()
);

export const driversStatus$ = driversStore.pipe(selectRequestStatus('drivers'));

export const deleteDriverStatus$ = driversStore.pipe(
  selectRequestStatus('delete-driver')
);

export const importDriverStatus$ = driversStore.pipe(
  selectRequestStatus('driver-import')
);

export const hasDrivers$ = driversStore.pipe(
  select((state) => state.hasDrivers)
);

export const driverRowsDescriptor$ = driversStore.pipe(
  select((state) => state.driverRowsDescriptor)
);

export const orphans$ = driversStore.pipe(
  select((state) => state.orphans),
  filterNil()
);

export const tableReload$ = new Subject<void>();
export const currentStepReset$ = new Subject<number>();
export const currentStepSave$ = new Subject<number>();

export const importResponse$ = driversStore.pipe(
  select((state) => state.importResponse)
);

export function updateHasDrivers(hasDrivers: boolean) {
  driversStore.update((state) => ({
    ...state,
    hasDrivers,
  }));
}

export function updateCreatingNewDriver(creatingNewDriver: boolean) {
  driversStore.update((state) => ({
    ...state,
    creatingNewDriver,
  }));
}

export function updatePrefilledDriverId(prefilledDriverId: null | string) {
  driversStore.update((state) => ({
    ...state,
    prefilledDriverId,
  }));
}

export function updateOrphans(orphans: Map<string, OrphanedResource> | null) {
  driversStore.update((state) => ({
    ...state,
    orphans,
  }));
}

export function updateLinkedData(
  linkedData: Map<string, DriverVehicleLink> | null
) {
  driversStore.update(
    (state) => ({
      ...state,
      linkedData,
    }),
    updateRequestStatus('linked-data', 'success')
  );
}

export const driversGroupsUpdateStatus$ = driversStore.pipe(
  selectRequestStatus('edit-drivers-groups')
);

export const linkedDataStatus$ = driversStore.pipe(
  selectRequestStatus('linked-data')
);

export function updateDrivers(drivers: Driver[], totalDrivers: number) {
  driversStore.update(
    (state) => ({
      ...state,
      totalDrivers,
    }),
    setEntities(drivers),
    updateRequestStatus('drivers', 'success')
  );
}

export function updateOffset(offset: number) {
  driversStore.update((state) => ({
    ...state,
    offset,
  }));
}

export function updateSort(
  sortExpression: string,
  sortDirection: '' | 'asc' | 'desc'
) {
  driversStore.update((state) => ({
    ...state,
    sortExpression,
    sortDirection,
  }));
}

export function updateQuery(query: string) {
  driversStore.update((state) => ({
    ...state,
    query,
  }));
}

export function updateDriversRowsDescriptor(
  driverRowsDescriptor: RowsDescriptor
) {
  driversStore.update((state) => ({
    ...state,
    driverRowsDescriptor,
  }));
}

export function updateSelectedDriver(selectedDriver: Driver | null) {
  driversStore.update(
    (state) => ({
      ...state,
      selectedDriver,
    }),
    updateRequestStatus('driver-details', 'success')
  );
}

export function updateSelectedDriversGroups(
  selectedDriversGroups: DriverGroup[]
) {
  driversStore.update((state) => ({
    ...state,
    selectedDriversGroups,
  }));
}

export const driverDriverGroupsStatus$ = driversStore.pipe(
  selectRequestStatus('drivers-groups')
);

export function updateSelectedDriversGroupIds(
  selectedDriversGroupIds: number[]
) {
  driversStore.update(
    (state) => ({
      ...state,
      selectedDriversGroupIds,
    }),
    updateRequestStatus('drivers-groups', 'success')
  );
}

export const driverDetailsStatus$ = driversStore.pipe(
  selectRequestStatus('driver-details'),
  shareReplay({ bufferSize: 1, refCount: true })
);

export function updateDriverUpdateStatus() {
  driversStore.update(updateRequestStatus('driver-update', 'success'));
}

export const driverUpdateStatus$ = driversStore.pipe(
  selectRequestStatus('driver-update')
);

export function resetDriversStatus() {
  driversStore.update(updateRequestStatus('drivers', 'idle'));
}

export function resetDriverUpdatesStatus() {
  driversStore.update(updateRequestStatus('driver-update', 'idle'));
}

export function updateDriverImportStatus() {
  driversStore.update(updateRequestStatus('driver-import', 'success'));
}

export function updateImportResponse(importResponse: DriversImportResponse) {
  driversStore.update((state) => ({
    ...state,
    importResponse,
  }));
}

export function updateDriverErrorMessage(driverErrorMessage: null | string) {
  driversStore.update((state) => ({
    ...state,
    driverErrorMessage,
  }));
}

export function updateTableReload() {
  tableReload$.next();
}

export function updateCurrentStepReset(currentStep: number) {
  currentStepReset$.next(currentStep);
}

export function updateCurrentStepSave(currentStep: number) {
  currentStepSave$.next(currentStep);
}

export function clearSelectedDriver() {
  driversStore.update((state) => ({
    ...state,
    selectedDriver: null,
  }));
}

export function clearSelectedDriverGroups() {
  driversStore.update((state) => ({
    ...state,
    selectedDriversGroups: [],
    selectedDriversGroupIds: [],
  }));
}

export function updateDriversGroupsStatus() {
  driversStore.update(updateRequestStatus('edit-drivers-groups', 'success'));
}

export function updateDeleteDriverStatus() {
  driversStore.update(updateRequestStatus('delete-driver', 'success'));
}

export const driverStoreSnapshot = () => driversStore.getValue();

export const driverEntitiesSnapshot = () =>
  driversStore.query(getAllEntities());

export function clearDriverStore() {
  emitOnce(() => {
    driversStore.update(clearRequestsStatus());
    driversStore.reset();
  });
}
